/**
 * The project's statistics as the app's «Статистика» gives them (`RoomStatisticsPopup`, `RoomInfoData`, `LevelInfoData`):
 * for each room its size, volume, doors and windows, floor and ceiling, walls with and without their openings, the
 * perimeter, plinths and ceiling moldings, the openings' slopes and the finishes by area; for a floor and for the whole
 * building the same summed, with the rooms by type and the floor area with and without the walls. Prices are not
 * counted: the app shows them to its paying users from its shop, which the web does not have.
 *
 * The room's faces are the floor's outline moved in by half of each wall (`roomFloorPolygon`), each with the wall along
 * it: its height, the finish on the side the room sees and — on that side being the wall's front, where the web draws
 * them — its plinth and molding. A hidden wall bounds the room and has no face to finish.
 */
import type { Level, Opening, Room, Scene, Surface, Vec2, Wall } from '../core/hp/types';
import { contains } from '../core/geometry/edit';
import { edgeWalls, floorArea, roomFloorPolygon, roomsBesideWall } from '../core/geometry/roomFloor';
import { openingTwins } from '../core/geometry/openingTwins';
import { openingKind } from './openingProperties';
import { knownModelTags } from './modelTags';
import { roomTypeTitle } from './roomTypes';
import { t } from '../i18n/I18n';
import { areaWithHeadroom, ceilingSurfaceAreas, ceilingVolume, FIVE_FEET, profileAlong, profileArea, roomCeiling, SEVEN_FEET, slopeSurface } from './roomCeiling';

/** A finish and the area it covers. */
export interface FinishArea { key: string; caption: string; surface?: Surface; area: number }

export interface SpaceStatistics {
  title: string;
  /** The floor's extent inside the walls, metres. */
  width: number;
  depth: number;
  volume: number;
  doors: number;
  windows: number;
  floorArea: number;
  ceilingArea: number;
  doorArea: number;
  windowArea: number;
  wallsArea: number;
  wallsWithoutOpenings: number;
  perimeter: number;
  ceilingPerimeter: number;
  plinths: number;
  moldings: number;
  doorSlopes: number;
  windowSlopes: number;
  windowSlopesNoSill: number;
  /** Of the ceiling, what a mansard slopes (`roomCeiling.ts`): its slopes, the tops of its niches and their sides. */
  slopeArea: number;
  /**
   * A room under a mansard measured as ANSI Z765 measures a US home's finished area: the floor with at least 5 ft of
   * ceiling over it (`counted`), and of that the floor with at least 7 ft (`tall`), which must be half of it or more.
   */
  sloped?: { counted: number; tall: number };
  wallFinishes: FinishArea[];
  floorFinishes: FinishArea[];
  ceilingFinishes: FinishArea[];
}

export interface SummaryStatistics extends SpaceStatistics {
  rooms: { bedroom: number; bathroom: number; dining: number; garage: number; kitchen: number; other: number };
  floorWithoutWalls: number;
  floorWithInteriorWalls: number;
  floorWithWalls: number;
  livingArea: number;
  exteriorPerimeter: number;
}

const length = (a: Vec2, b: Vec2) => Math.hypot(b.x - a.x, b.z - a.z);
const dot = (a: Vec2, b: Vec2) => a.x * b.x + a.z * b.z;

/** What kind an opening is, by its catalog tags once known. */
const kindOf = (opening: Opening) => openingKind(opening, knownModelTags(opening.modelRef.id) ?? []);

/** OpeningSlopesMetrics: a window's slopes go round it, a door's and an arch's leave the threshold out. */
function slopes(opening: Opening, kind: ReturnType<typeof kindOf>) {
  return { door: kind === 'door' || kind === 'arch' ? opening.width + 2 * opening.height : 0,
    window: kind === 'window' ? 2 * opening.width + 2 * opening.height : 0,
    windowNoSill: kind === 'window' ? opening.width + 2 * opening.height : 0 };
}

/** A finish's name and the key finishes of the same look share. */
function finishOf(surface: Surface | undefined, fallback: string): { key: string; caption: string } {
  if (!surface) return { key: 'none', caption: fallback };
  const key = JSON.stringify([surface.texture ?? null, surface.customTexture ?? null, surface.texture || surface.customTexture ? null : surface.color ?? null]);
  return { key, caption: surface.caption?.trim() || (surface.customTexture ? t('#StatisticsFinishCustomImage') : surface.texture ? t('Texture') : t('Color')) };
}

function addFinish(list: FinishArea[], surface: Surface | undefined, area: number, fallback: string): void {
  if (!(area > 1e-6)) return;
  const { key, caption } = finishOf(surface, fallback);
  const found = list.find(finish => finish.key === key);
  if (found) found.area += area; else list.push({ key, caption, surface, area });
}

/** A room's statistics (`RoomInfoData`). */
export function roomStatistics(level: Level, room: Room, title = room.name || roomTypeTitle(room.roomType) || t('#RoomAll')): SpaceStatistics {
  const floor = roomFloorPolygon(level, room), walls = edgeWalls(level, room), area = floorArea(level, room);
  const xs = floor.map(p => p.x), zs = floor.map(p => p.z);
  // Under a mansard the room is as high as its ceiling over each point (`roomCeiling.ts`); elsewhere its flat ceiling's height.
  const ceiling = roomCeiling(level, room);
  const surfaces = ceiling ? ceilingSurfaceAreas(ceiling) : null;
  const slopeArea = surfaces ? surfaces.slopes.reduce((sum, slope) => sum + slope.area, 0) : 0;
  const stats: SpaceStatistics = { title, width: Math.max(...xs) - Math.min(...xs), depth: Math.max(...zs) - Math.min(...zs),
    volume: ceiling ? ceilingVolume(ceiling) : area * room.height, doors: 0, windows: 0, floorArea: area, ceilingArea: surfaces ? surfaces.flat + slopeArea : area,
    doorArea: 0, windowArea: 0, wallsArea: 0, wallsWithoutOpenings: 0, perimeter: 0, ceilingPerimeter: 0, plinths: 0, moldings: 0,
    doorSlopes: 0, windowSlopes: 0, windowSlopesNoSill: 0, slopeArea,
    ...(ceiling ? { sloped: { counted: areaWithHeadroom(ceiling, FIVE_FEET), tall: areaWithHeadroom(ceiling, SEVEN_FEET) } } : {}),
    wallFinishes: [], floorFinishes: [], ceilingFinishes: [] };
  const counted = new Set<string>();
  room.polygon.forEach((a, index) => {
    const b = room.polygon[(index + 1) % room.polygon.length], wall = walls[index];
    const face = length(floor[index], floor[(index + 1) % floor.length]);
    // Under a mansard a face reaches only as high as the ceiling over it: its height along it, a hair inside the room.
    const p0 = floor[index], p1 = floor[(index + 1) % floor.length], inward = { x: -(p1.z - p0.z) / (face || 1), z: (p1.x - p0.x) / (face || 1) };
    const into = contains(floor, { x: (p0.x + p1.x) / 2 + inward.x * 1e-3, z: (p0.z + p1.z) / 2 + inward.z * 1e-3 }) ? 1e-6 : -1e-6;
    const profile = ceiling && face > 1e-9 ? profileAlong(ceiling, { x: p0.x + inward.x * into, z: p0.z + inward.z * into }, { x: p1.x + inward.x * into, z: p1.z + inward.z * into }) : null;
    stats.perimeter += face;
    if (!wall) return;
    const height = Math.min(wall.height, room.height) || room.height;
    if (wall.height >= room.height - 1e-3) stats.ceilingPerimeter += face;
    // The openings of this stretch of the wall.
    const t0 = dot({ x: a.x - wall.start.x, z: a.z - wall.start.z }, wall.dir), t1 = dot({ x: b.x - wall.start.x, z: b.z - wall.start.z }, wall.dir);
    const lo = Math.min(t0, t1) - 1e-3, hi = Math.max(t0, t1) + 1e-3;
    const openings = level.openings.filter(o => o.wallId === wall.id && o.offset >= lo && o.offset <= hi);
    let openingArea = 0, doorWidths = 0;
    // An opening's place along the face: the face runs along the wall one way or the other.
    const sense = dot({ x: p1.x - p0.x, z: p1.z - p0.z }, wall.dir) >= 0 ? 1 : -1, from = dot({ x: p0.x - wall.start.x, z: p0.z - wall.start.z }, wall.dir);
    for (const opening of openings) {
      const kind = kindOf(opening), own = opening.width * opening.height;
      // Under a slope only what stands below it is cut from the wall: a window lying in the slope cuts nothing of it.
      if (profile) {
        const at = (sense * (opening.offset - from)), half = opening.width / 2;
        openingArea += profileArea(profile, opening.sill + opening.height, at - half, at + half, opening.sill);
      } else openingArea += own;
      if (kind === 'door' || kind === 'arch') doorWidths += opening.width;
      if (counted.has(opening.id)) continue;
      counted.add(opening.id);
      if (kind === 'door') { stats.doors++; stats.doorArea += own; }
      if (kind === 'window') { stats.windows++; stats.windowArea += own; }
      const slope = slopes(opening, kind);
      stats.doorSlopes += slope.door; stats.windowSlopes += slope.window; stats.windowSlopesNoSill += slope.windowNoSill;
    }
    if (wall.hidden) return;
    const faceArea = profile ? profileArea(profile, height) : face * height, bare = Math.max(0, faceArea - openingArea);
    stats.wallsArea += faceArea; stats.wallsWithoutOpenings += bare;
    // The side of the wall the room sees: its front when its normal points into the room.
    const middle = { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 };
    const front = contains(room.polygon, { x: middle.x + wall.normalIn.x * .02, z: middle.z + wall.normalIn.z * .02 });
    addFinish(stats.wallFinishes, front ? wall.frontSurface : wall.backSurface ?? wall.frontSurface, bare, t('#NoFinishWall'));
    if (front && wall.plinth) stats.plinths += Math.max(0, face - doorWidths);
    if (front && wall.molding) stats.moldings += face;
  });
  addFinish(stats.floorFinishes, room.floorSurface, area, t('Floor'));
  if (ceiling && surfaces) {
    addFinish(stats.ceilingFinishes, room.ceilingSurface, surfaces.flat, t('Ceiling'));
    surfaces.slopes.forEach((slope, index) => addFinish(stats.ceilingFinishes, slopeSurface(ceiling.slopes[index]), slope.area, t('#StatisticsFinishSlope')));
  } else addFinish(stats.ceilingFinishes, room.ceilingSurface, area, t('Ceiling'));
  return stats;
}

const ROOM_KINDS: Record<string, keyof SummaryStatistics['rooms']> = { bedroom: 'bedroom', bathroom: 'bathroom', dining: 'dining', diningroom: 'dining',
  garage: 'garage', kitchens: 'kitchen', kitchen: 'kitchen' };
const kindOfRoom = (room: Room) => ROOM_KINDS[room.roomType.trim().toLowerCase()] ?? 'other';

function sumFinishes(target: FinishArea[], source: readonly FinishArea[]): void {
  for (const finish of source) {
    const found = target.find(value => value.key === finish.key);
    if (found) found.area += finish.area; else target.push({ ...finish });
  }
}

/** Whether a wall has a room on one side only: an outside wall of the floor. */
function exterior(level: Level, wall: Wall): boolean {
  const { front, back } = roomsBesideWall(level, wall);
  return front !== back;
}

/** A floor's summary (`LevelInfoData`), its rooms' statistics summed and its walls and openings counted once each. */
export function levelStatistics(level: Level, title: string): SummaryStatistics {
  const rooms = level.rooms.filter(room => room.polygon.length >= 3).map(room => ({ room, stats: roomStatistics(level, room) }));
  const points = level.walls.length ? level.walls.flatMap(wall => [wall.start, wall.end]) : level.rooms.flatMap(room => room.polygon);
  const xs = points.map(p => p.x), zs = points.map(p => p.z), outer = Math.max(0, ...level.walls.map(wall => wall.thickness));
  const summary: SummaryStatistics = { title, width: points.length ? Math.max(...xs) - Math.min(...xs) + outer : 0, depth: points.length ? Math.max(...zs) - Math.min(...zs) + outer : 0,
    volume: 0, doors: 0, windows: 0, floorArea: 0, ceilingArea: 0, doorArea: 0, windowArea: 0, wallsArea: 0, wallsWithoutOpenings: 0,
    perimeter: 0, ceilingPerimeter: 0, plinths: 0, moldings: 0, doorSlopes: 0, windowSlopes: 0, windowSlopesNoSill: 0, slopeArea: 0,
    wallFinishes: [], floorFinishes: [], ceilingFinishes: [],
    rooms: { bedroom: 0, bathroom: 0, dining: 0, garage: 0, kitchen: 0, other: 0 },
    floorWithoutWalls: 0, floorWithInteriorWalls: 0, floorWithWalls: 0, livingArea: 0, exteriorPerimeter: 0 };
  let axisArea = 0;
  for (const { room, stats } of rooms) {
    const kind = kindOfRoom(room);
    summary.rooms[kind]++;
    if (room.roomType.trim().toLowerCase() !== 'outdoor') summary.volume += stats.volume;
    summary.floorArea += stats.floorArea; summary.ceilingArea += stats.ceilingArea; summary.slopeArea += stats.slopeArea;
    summary.wallsArea += stats.wallsArea; summary.wallsWithoutOpenings += stats.wallsWithoutOpenings;
    summary.perimeter += stats.perimeter; summary.ceilingPerimeter += stats.ceilingPerimeter;
    summary.plinths += stats.plinths; summary.moldings += stats.moldings;
    if (kind !== 'garage' && room.roomType.trim().toLowerCase() !== 'outdoor') summary.livingArea += stats.floorArea;
    sumFinishes(summary.wallFinishes, stats.wallFinishes); sumFinishes(summary.floorFinishes, stats.floorFinishes); sumFinishes(summary.ceilingFinishes, stats.ceilingFinishes);
    axisArea += Math.abs(room.polygon.reduce((sum, p, i) => { const q = room.polygon[(i + 1) % room.polygon.length]; return sum + p.x * q.z - q.x * p.z; }, 0) / 2);
  }
  summary.floorWithoutWalls = summary.floorArea;
  // A room's outline runs along the walls' axes: inner walls are counted whole between two rooms, outside walls by half.
  const outsideHalf = level.walls.filter(wall => !wall.hidden && exterior(level, wall)).reduce((sum, wall) => sum + wall.length * wall.thickness / 2, 0);
  summary.floorWithInteriorWalls = Math.max(summary.floorWithoutWalls, axisArea - outsideHalf);
  summary.floorWithWalls = axisArea + outsideHalf;
  summary.exteriorPerimeter = level.walls.filter(wall => exterior(level, wall)).reduce((sum, wall) => sum + wall.length, 0);
  // Each opening once, a door between two rooms of an app project counted by one of its copies.
  const twins = openingTwins(level), seen = new Set<string>();
  for (const opening of level.openings) {
    if (seen.has(opening.id)) continue;
    seen.add(opening.id);
    const twin = twins.get(opening.id);
    if (twin) seen.add(twin);
    const kind = kindOf(opening), own = opening.width * opening.height, slope = slopes(opening, kind);
    if (kind === 'door') { summary.doors++; summary.doorArea += own; }
    if (kind === 'window') { summary.windows++; summary.windowArea += own; }
    summary.doorSlopes += slope.door; summary.windowSlopes += slope.window; summary.windowSlopesNoSill += slope.windowNoSill;
  }
  return summary;
}

/** The building's summary (`BuildingInfoData`): its floors' summaries summed, its outside measured over all of them. */
export function buildingStatistics(scene: Scene, levelTitle: (level: Level, index: number) => string): SummaryStatistics {
  const levels = scene.levels.map((level, index) => levelStatistics(level, levelTitle(level, index)));
  const total: SummaryStatistics = { ...levelStatistics({ ...scene.levels[0], rooms: [], walls: [], openings: [], items: [] }, t('Building')),
    width: Math.max(0, ...levels.map(level => level.width)), depth: Math.max(0, ...levels.map(level => level.depth)) };
  for (const level of levels) {
    for (const key of ['volume', 'doors', 'windows', 'floorArea', 'ceilingArea', 'slopeArea', 'doorArea', 'windowArea', 'wallsArea', 'wallsWithoutOpenings', 'perimeter',
      'ceilingPerimeter', 'plinths', 'moldings', 'doorSlopes', 'windowSlopes', 'windowSlopesNoSill', 'floorWithoutWalls', 'floorWithInteriorWalls',
      'floorWithWalls', 'livingArea', 'exteriorPerimeter'] as const) total[key] += level[key];
    for (const kind of Object.keys(total.rooms) as (keyof SummaryStatistics['rooms'])[]) total.rooms[kind] += level.rooms[kind];
    sumFinishes(total.wallFinishes, level.wallFinishes); sumFinishes(total.floorFinishes, level.floorFinishes); sumFinishes(total.ceilingFinishes, level.ceilingFinishes);
  }
  return total;
}

/** The rooms of a floor as the app's filter lists them: the largest first, a name two rooms share told apart by area. */
export function statisticRooms(level: Level): { room: Room; title: string }[] {
  const named = level.rooms.filter(room => room.polygon.length >= 3).map(room => ({ room, name: room.name || roomTypeTitle(room.roomType) || t('#RoomAll'), area: floorArea(level, room) }))
    .sort((a, b) => b.area - a.area);
  return named.map(({ room, name, area }) => ({ room, title: named.filter(other => other.name === name).length > 1 ? t('#StatisticsRoomNameWithArea', name, area.toFixed(1)) : name }));
}
